<template>
  <section
    v-if="showSection"
    id="phonebook"
    class="lpu-phonebook d-flex flex-column"
    :class="isMobile ? 'lpu-phonebook--mobile ga-4' : 'ga-6'"
    data-qa="lpu_phonebook"
  >
    <h2
      ref="titleRef"
      class="text--text ma-0"
      :class="isMobile ? 'text-h6' : 'text-h5'"
      data-qa="lpu_phonebook_title"
    >
      {{ tStatic('Телефонный справочник') }}
    </h2>

    <div
      class="lpu-phonebook__lists d-flex flex-column"
      :class="isMobile ? 'ga-4' : 'ga-6'"
    >
      <PhonebookDepartment
        v-for="department in collapsedDepartments"
        :key="department.key"
        :department="department"
      />

      <v-expand-transition>
        <div
          v-if="isExpanded && expandedDepartments.length"
          class="d-flex flex-column"
          :class="isMobile ? 'ga-4' : 'ga-6'"
        >
          <PhonebookDepartment
            v-for="department in expandedDepartments"
            :key="department.key"
            :department="department"
          />
        </div>
      </v-expand-transition>
    </div>

    <v-btn
      v-if="hasHiddenDepartments"
      depressed
      height="52"
      :block="isMobile"
      class="lpu-phonebook__toggle branding-btn branding-btn--primary rounded-20 text-subtitle-1 text-none"
      :class="isMobile ? 'w-100' : 'align-self-center'"
      data-qa="lpu_phonebook_toggle"
      @click="toggleExpanded"
    >
      {{ toggleLabel }}
    </v-btn>
  </section>
</template>

<script lang="ts" setup>
import { computed, nextTick, ref } from 'vue'
import useIsMobile from 'src/composables/useIsMobile'
import useContextStore from 'src/store/useContextStore'
import tDynamic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tDynamic'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'
import type { ContextStore } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/index/types'
import { PHONEBOOK_COLLAPSED_COUNT } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPhonebook/constants'
import type { PhonebookDepartmentView } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPhonebook/types'
import unwrapGosPhones from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPhonebook/functions/unwrapGosPhones'
import PhonebookDepartment from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPhonebook/components/PhonebookDepartment.vue'

const isMobile = useIsMobile()
const { context }: ContextStore = useContextStore()
const isExpanded = ref(false)
const titleRef = ref<HTMLElement>()

const departments = computed<PhonebookDepartmentView[]>(() => (
  unwrapGosPhones(context.gos_phones).map((department, index) => ({
    key: String(department.specialities[0]?.id ?? index),
    name: tDynamic(department, 'name'),
    specialities: department.specialities,
  }))
))
const showSection = computed(() => (
  Boolean(departments.value.length)
  && !context.lpu.paid
))
const hasHiddenDepartments = computed(() => departments.value.length > PHONEBOOK_COLLAPSED_COUNT)
const collapsedDepartments = computed(() => (
  hasHiddenDepartments.value
    ? departments.value.slice(0, PHONEBOOK_COLLAPSED_COUNT)
    : departments.value
))
const expandedDepartments = computed(() => (
  hasHiddenDepartments.value
    ? departments.value.slice(PHONEBOOK_COLLAPSED_COUNT)
    : []
))
const toggleLabel = computed(() => {
  if (isExpanded.value) {
    return isMobile.value
      ? tStatic('Свернуть')
      : tStatic('Скрыть')
  }

  return isMobile.value
    ? tStatic('Развернуть')
    : tStatic('Показать все контакты')
})

async function toggleExpanded() {
  isExpanded.value = !isExpanded.value

  if (!isExpanded.value && isMobile.value) {
    await nextTick()
    titleRef.value?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }
}
</script>

<style scoped lang="scss">
.lpu-phonebook {
  /* Компенсирует row-gap во время enter/leave expand-transition */
  &__lists > :deep(.expand-transition-leave-active),
  &__lists > :deep(.expand-transition-enter) {
    margin-bottom: -24px;
  }

  &--mobile {
    .lpu-phonebook__lists > :deep(.expand-transition-leave-active),
    .lpu-phonebook__lists > :deep(.expand-transition-enter) {
      margin-bottom: -16px;
    }
  }

  &__toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
